import { DestroyRef, inject, Injectable, OnDestroy } from '@angular/core';
import { CommunicatorService } from 'app/shared/services/communicator.service';
import { RuleEvaluatorService } from 'app/shared/services/rules/rule-evaluator.service';
import { ApplicationHelperService } from '../forms/application-helper.service';
import { CommunicatorCommandsEnum, CommunicatorModel, VisibilityChangeModel } from 'app/shared/services/model/communicatorModel';
import { ControlHelperService } from '../forms/control-helper.service';
import { VisibilityService } from './visibility.service'
import { Observer, Subscription } from 'rxjs';
import { DataContainer, IValuePersistableControl, Qualifier, RuleType, SurveyResult, iApplyFormControl, isContainerControl, isControlWithObjectData } from 'app/shared/model';
import { DataSharingService } from 'app/shared/services/data-sharing.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Injectable()
export class ControlVisibilityService implements OnDestroy
{
    private readonly destroyRef = inject(DestroyRef);
    private readonly _communicatorService: CommunicatorService = inject(CommunicatorService);
    private readonly _controlListFunctionsService: ControlHelperService = inject(ControlHelperService);
    private readonly _ruleEvaluatorService: RuleEvaluatorService = inject(RuleEvaluatorService);
    private readonly _applicationHelperService: ApplicationHelperService = inject(ApplicationHelperService);
    private readonly _dataSharingService: DataSharingService = inject(DataSharingService);

    _unwatchFunctions: Subscription[] = [];

    constructor() {
        let sub = this._communicatorService.applicationChangeObj$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (x) => {
                    if (x.command == CommunicatorCommandsEnum.proxyObjectChanged && !x.value) {
                        this.removeWatchers();
                    }
                },
                complete: () => { this.removeWatchers(); }
            })

        this._unwatchFunctions.push(sub);
    }

    private handler(control: iApplyFormControl, application: DataContainer, qualifier: Qualifier, controlList: iApplyFormControl[], surveyResult) {
        let isVisible = this.isVisible(control, application, qualifier, controlList, surveyResult);
        this._communicatorService.sendVisibilityChangeMessage(new VisibilityChangeModel(control.id, isVisible, qualifier));
    };

    ngOnDestroy() {
        this.removeWatchers();
    }

    removeWatchers(array?: Subscription[]) {
        let currentWatch = array || this._unwatchFunctions;
        if (!currentWatch || !Array.isArray(currentWatch)) {
            return;
        }

        for (let i = currentWatch.length - 1; i >= 0; i--) {
            currentWatch[i].unsubscribe();
        }
        currentWatch.splice(0); // clear array
    };

    /**
     * @ngdoc method
     * @name ApplicationEditor.services:ControlVisibilityService#isVisible
     * @methodOf ApplicationEditor.services:ControlVisibilityService
     *
     * @description
     *
     * Determines if a control or its parent has a visibility rule applied to it.
     *
     * @param {object} control The control to test visibility for.
     * @param {object} application The application data entered by the user.
     * @param {string} qualifier The path to the position to begin the search from.
     * @param {array} controlList The list of controls.
     * @param {object} surveyResult The survey result if any.
     *
     * @returns {boolean} `true` if the control is visible, otherwise `false`.
     */
    isVisible(
        control: iApplyFormControl,
        application: DataContainer,
        qualifier: Qualifier,
        controlList: iApplyFormControl[],
        surveyResult
    ): boolean {
        let parentVisible = true;
        if (controlList && control.parentControlId) {
            let parentControl = this._controlListFunctionsService.findById(controlList, control.parentControlId);
            if (parentControl) {
                let newQualifier = qualifier.toString(),
                    indexOfControlName = newQualifier.indexOf(parentControl.name);
                if (indexOfControlName >= 0) {
                    newQualifier = newQualifier.substring(0, indexOfControlName - 1);
                }
                parentVisible = this.isVisible(parentControl, application, new Qualifier(newQualifier), controlList, null);
            }
        }

        let rule = control.visibilityRule;
        if (!rule) {
            return parentVisible;
        }

        return parentVisible &&
            (rule.ruleType === RuleType.Show) ===
            this._ruleEvaluatorService.evaluate(rule, application, qualifier, surveyResult, controlList);
    };

    /**
     * @ngdoc method
     * @name ApplicationEditor.services:ControlVisibilityService#createVisibilityWatch
     * @methodOf ApplicationEditor.services:ControlVisibilityService
     *
     * @description
     *
     * Creates a watch on a control's user-entered application value to show/hide
     * controls based on this value.
     *
     * @param {array} controlList The list of controls.
     * @param {object} application The application data entered by the user.
     * @param {string} qualifier The path to the position to begin the search from.
     * @param {object} scope The scope to watch.
     * @param {object} surveyResult The survey result.
     */
    createVisibilityWatch(
        controlList: iApplyFormControl[],
        application: DataContainer,
        qualifier: Qualifier,
        visibilityService: VisibilityService,
        surveyResult: SurveyResult,
        scopedControls: iApplyFormControl[]
    ) {
        if (!controlList?.length) {
            return;
        }

        let self = this,
            setWatchFn = (expression: string, controlName: string, ctrl: iApplyFormControl, initPath: Qualifier, fuzzyMatching: boolean) => {
                let unwatch = self._communicatorService.applicationChangeObj$.subscribe(<Partial<Observer<CommunicatorModel>>>{
                    expression: expression,
                    prop: controlName,
                    next: function (e) {
                        // fuzzyMatching only requires to match the first part of the path:
                        // e.g. expression: Repeater works for both Repeater[0], Repeater[1]
                        const validPath = fuzzyMatching ? e.path.toString().startsWith(expression) : e.path.toString() == expression;
                        if (validPath && e.prop == controlName) {
                            self.handler(ctrl, application, qualifier, scopedControls, surveyResult);
                        }
                    }
                });

                if (visibilityService) {
                    visibilityService.subscribe(unwatch, ctrl.name, initPath);
                } else {
                    self._unwatchFunctions.push(unwatch);
                }
            };

        // for (let i = 0; i < controlList.length; i++) {
        //     let control = controlList[i];
        //     ...
        // }
        for (let control of controlList) {
            if (control.visibilityRule) {
                let j, dependencies = [], dependenciesToCollections = [];

                this._ruleEvaluatorService.findRuleDependencies(control.visibilityRule, dependencies, dependenciesToCollections);

                for (j = 0; j < dependencies.length; j++) {
                    let fieldName = dependencies[j].replace(/^([^.]*)[.]*([^.]*)[.]Total$/, '$1');
                    fieldName = fieldName.replace(/^([^.]*)[.]*([^.]*)[.]RowCount$/, '$1');

                    let containerPath = this._applicationHelperService.findFieldContainer(qualifier, fieldName, application, this._dataSharingService.extendedControls)
                        || new Qualifier();

                    let fuzzyMatching = false;
                    if (fieldName !== dependencies[j]) {
                        // Need to handle cases for Total and RowCount: e.g. Repeater.Text.Total or Repeater.RowCount
                        const result = this.getFieldAndPathFromRuleDependency(dependencies[j], fieldName, containerPath);
                        fieldName = result.fieldName;
                        containerPath = result.path;
                        fuzzyMatching = result.fuzzyMatching;
                    }

                    setWatchFn(containerPath.toString(), fieldName, control, qualifier, fuzzyMatching);
                }

                for (j = 0; j < dependenciesToCollections.length; j++) {
                    let repeater = this._controlListFunctionsService.findByName(this._dataSharingService.extendedControls, dependenciesToCollections[j]),
                        arrPath = [],
                        depName = dependenciesToCollections[j];

                    while (repeater?.parentControlId) {
                        let r = this._controlListFunctionsService.findNearestRepeater(this._dataSharingService.extendedControls, repeater);
                        if (!r) {
                            break;
                        }
                        repeater = r;
                        arrPath.unshift(repeater.name)
                    }

                    if (repeater) {
                        let unwatch = self._communicatorService.applicationChangeObj$.subscribe(<Partial<Observer<CommunicatorModel>>>{
                            expression: arrPath,
                            prop: depName,
                            next: function (e) {
                                if (e.prop == depName ||
                                    (e.prop == "length" && e.path.length <= arrPath.length)) {
                                    self.handler(control, application, qualifier, scopedControls, surveyResult);
                                }
                            }
                        });

                        if (visibilityService) {
                            visibilityService.subscribe(unwatch, control.name, qualifier);
                        } else {
                            self._unwatchFunctions.push(unwatch);
                        }
                    }
                }
            }

            if (isContainerControl(control)) {
                this.createVisibilityWatch(control.controls, application, qualifier, visibilityService, surveyResult, scopedControls);
            }
        }
    };

    initializeFieldValue(ctrl: iApplyFormControl, container) {
        let safeFields = ['creditcard', 'calculation'];
        if (
            safeFields.includes(ctrl.type) ||
            (<IValuePersistableControl>ctrl).persistValue ||
            ctrl.isHidden ||
            this._dataSharingService.externalPopulatedFields?.includes(ctrl.name)
        ) {
            return;
        }

        if (!ctrl.lastIsVisibleValue) {
            if (isControlWithObjectData(ctrl)) {
                this._applicationHelperService.setFieldWithMetadataValue(container, ctrl, null);
            } else {
                this._applicationHelperService.setFieldValue(container, ctrl.name, null);
            }
        } else {
            this._applicationHelperService.setFieldDefaultValue(container, ctrl);
        }
    };

    // handle for special cases for createVisibilityWatch e.g RowCount, Total for Repeater controls
    // if RowCount: return { fieldName: 'length', path: Repeater, fuzzyMatching: false }
    // if RowCount: return { fieldName: The total field, path: Repeater or Repeater[0].Child_Repeater, fuzzyMatching: true }
    getFieldAndPathFromRuleDependency(dependency: string, fieldName: string, containerPath: Qualifier)
        : { fieldName: string, path: Qualifier, fuzzyMatching: boolean } {
        // dependency: e.g. Repeater.Text.Total or Repeater.RowCount
        const splitArr = dependency.split('.');

        // handle for RowCount
        if (splitArr.length === 2 && splitArr[1] === 'RowCount') {
            return {
                fieldName: 'length',
                path: new Qualifier(containerPath.toString()).addPath(fieldName, null),
                fuzzyMatching: false
            };
        }

        // handle for Total
        if (splitArr.length === 3 && splitArr[2] === 'Total') {
            return {
                fieldName: splitArr[1],
                path: new Qualifier(containerPath.toString()).addPath(fieldName, null),
                fuzzyMatching: true
            };
        }

        return {
            fieldName: fieldName,
            path: containerPath,
            fuzzyMatching: false
        };
    }
}
